import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { ForgotPasswordTokenDto } from "../../../model/user/dto/ForgotPasswordTokenDto";
import { useAppDispatch } from "../../../redux/store";
import { userActions } from "../../../redux/user/user.slice";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { AtiraVerificationInput } from "../../../components/AtiraVerificationInput";
import { ForgotPasswordEmailDto } from "../../../model/user/dto/ForgotPasswordEmailDto";

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;
  margin-top: ${Spacing.m};

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

type ForgotPasswordTokenProps = {
  updateStep: VoidFunction;
};

export const ForgotPasswordToken: React.FC<ForgotPasswordTokenProps> = ({
  updateStep,
}) => {
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, getValues, setError } = useFormContext<
    ForgotPasswordTokenDto & ForgotPasswordEmailDto
  >();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const onResendToken = async () => {
    try {
      const email = getValues("email");

      await dispatch(userActions.resendForgotPasswordToken({ email })).unwrap();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const onNext = async () => {
    try {
      setLoading(true);
      await dispatch(
        userActions.forgotPasswordToken({ token: getValues("token") }),
      ).unwrap();

      AtiraToast.success(t("resetpassword.token.success"));
      updateStep();
    } catch (e: any) {
      AtiraToast.apiError(e, { autoClose: 6000 });
      console.log(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      width={"100%"}
      gap="s"
    >
      <Text color="gray" align="center">
        {t("resetpassword.token.title")}
      </Text>
      <Controller
        name="token"
        control={control}
        rules={{
          required: {
            value: true,
            message: t("common.fieldRequired"),
          },
          minLength: {
            value: 6,
            message: t("common.error.verificationCode.length"),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <AtiraVerificationInput
            value={value}
            onChange={onChange}
            valid={!error}
            errorMessage={error?.message}
            onResendToken={onResendToken}
          />
        )}
      />
      <StyledButton onClick={handleSubmit(onNext)} loading={loading}>
        {t("resetpassword.token.button")}
      </StyledButton>
    </Flex>
  );
};
